'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMarkovTrain, useWalkForward } from '@/hooks/useMarkov';
import type { MarkovTrainResponse, WalkForwardResult } from '@/types/markov.types';

// Form validation schema
const trainSchema = z.object({
  instrument: z.string().min(1, 'Instrument is required'),
  timeframe: z.string().min(1, 'Timeframe is required'),
  lookback_days: z.coerce.number().min(1).max(365).default(90),
  num_states: z.coerce.number().min(2).max(20).default(5),
  min_samples_per_state: z.coerce.number().min(10).max(1000).default(50),
  kelly_fraction: z.coerce.number().min(0).max(1).default(0.25),
});

const walkForwardSchema = z.object({
  instrument: z.string().min(1, 'Instrument is required'),
  timeframe: z.string().min(1, 'Timeframe is required'),
  train_bars: z.coerce.number().min(100).default(5000),
  test_bars: z.coerce.number().min(10).default(1000),
  num_folds: z.coerce.number().min(1).max(10).default(5),
});

type TrainFormData = z.infer<typeof trainSchema>;
type WalkForwardFormData = z.infer<typeof walkForwardSchema>;

export default function MarkovTrainPage() {
  const [trainResult, setTrainResult] = useState<MarkovTrainResponse | null>(null);
  const [walkForwardResult, setWalkForwardResult] = useState<WalkForwardResult | null>(null);

  const trainMutation = useMarkovTrain();
  const walkForwardMutation = useWalkForward();

  const trainForm = useForm<TrainFormData>({
    resolver: zodResolver(trainSchema),
    defaultValues: {
      instrument: 'EUR_USD',
      timeframe: '1H',
      lookback_days: 90,
      num_states: 5,
      min_samples_per_state: 50,
      kelly_fraction: 0.25,
    },
  });

  const walkForwardForm = useForm<WalkForwardFormData>({
    resolver: zodResolver(walkForwardSchema),
    defaultValues: {
      instrument: 'EUR_USD',
      timeframe: '1H',
      train_bars: 5000,
      test_bars: 1000,
      num_folds: 5,
    },
  });

  const onTrain = async (data: TrainFormData) => {
    try {
      const result = await trainMutation.mutateAsync(data);
      setTrainResult(result);
    } catch (error) {
      console.error('Training failed:', error);
    }
  };

  const onWalkForward = async (data: WalkForwardFormData) => {
    try {
      const result = await walkForwardMutation.mutateAsync(data);
      setWalkForwardResult(result);
    } catch (error) {
      console.error('Walk-forward failed:', error);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Markov Strategy Training</h1>
        <p className="text-muted-foreground mt-2">
          Train probabilistic Markov trading strategies with confidence intervals and regime detection
        </p>
      </div>

      <Tabs defaultValue="train" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="train">Quick Train</TabsTrigger>
          <TabsTrigger value="walkforward">Walk-Forward Validation</TabsTrigger>
        </TabsList>

        {/* Quick Training Tab */}
        <TabsContent value="train" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Train Markov Strategy</CardTitle>
              <CardDescription>
                Train a new Markov strategy on historical data with bracket stats and regime detection
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={trainForm.handleSubmit(onTrain)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="instrument">Instrument</Label>
                    <Input
                      id="instrument"
                      placeholder="EUR_USD"
                      {...trainForm.register('instrument')}
                    />
                    {trainForm.formState.errors.instrument && (
                      <p className="text-sm text-red-500">
                        {trainForm.formState.errors.instrument.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeframe">Timeframe</Label>
                    <Select
                      onValueChange={(value) => trainForm.setValue('timeframe', value)}
                      defaultValue={trainForm.watch('timeframe')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1M">1 Minute</SelectItem>
                        <SelectItem value="5M">5 Minutes</SelectItem>
                        <SelectItem value="15M">15 Minutes</SelectItem>
                        <SelectItem value="1H">1 Hour</SelectItem>
                        <SelectItem value="4H">4 Hours</SelectItem>
                        <SelectItem value="1D">1 Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lookback_days">Lookback Days</Label>
                    <Input
                      id="lookback_days"
                      type="number"
                      {...trainForm.register('lookback_days')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="num_states">Number of States</Label>
                    <Input
                      id="num_states"
                      type="number"
                      {...trainForm.register('num_states')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min_samples">Min Samples Per State</Label>
                    <Input
                      id="min_samples"
                      type="number"
                      {...trainForm.register('min_samples_per_state')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kelly_fraction">Kelly Fraction</Label>
                    <Input
                      id="kelly_fraction"
                      type="number"
                      step="0.01"
                      {...trainForm.register('kelly_fraction')}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={trainMutation.isPending}
                >
                  {trainMutation.isPending ? 'Training...' : 'Train Strategy'}
                </Button>
              </form>

              {trainMutation.isError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800">
                    Error: {trainMutation.error instanceof Error ? trainMutation.error.message : 'Training failed'}
                  </p>
                </div>
              )}

              {trainResult && (
                <div className="mt-6 space-y-4">
                  <h3 className="text-lg font-semibold">Training Results</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Status</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-green-600">{trainResult.status}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">States</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{trainResult.num_states}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Total Samples</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{trainResult.total_samples.toLocaleString()}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Sharpe Estimate</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">
                          {trainResult.bracket_stats.sharpe_estimate.toFixed(2)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Bracket Statistics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span>Win Rate:</span>
                        <span className="font-semibold">
                          {(trainResult.bracket_stats.win_rate * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Expectancy:</span>
                        <span className="font-semibold">
                          {trainResult.bracket_stats.expectancy.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Profit Factor:</span>
                        <span className="font-semibold">
                          {trainResult.bracket_stats.profit_factor.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Avg Win:</span>
                        <span className="font-semibold text-green-600">
                          {trainResult.bracket_stats.avg_win.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Avg Loss:</span>
                        <span className="font-semibold text-red-600">
                          {trainResult.bracket_stats.avg_loss.toFixed(4)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Regime Detection</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span>Regime:</span>
                        <span className="font-semibold">{trainResult.regime_detection.regime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Confidence:</span>
                        <span className="font-semibold">
                          {(trainResult.regime_detection.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Characteristics:</p>
                        <div className="flex flex-wrap gap-2">
                          {trainResult.regime_detection.characteristics.map((char, i) => (
                            <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                              {char}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Walk-Forward Validation Tab */}
        <TabsContent value="walkforward" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Walk-Forward Validation</CardTitle>
              <CardDescription>
                Out-of-sample performance testing with rolling train/test splits
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={walkForwardForm.handleSubmit(onWalkForward)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wf-instrument">Instrument</Label>
                    <Input
                      id="wf-instrument"
                      placeholder="EUR_USD"
                      {...walkForwardForm.register('instrument')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wf-timeframe">Timeframe</Label>
                    <Select
                      onValueChange={(value) => walkForwardForm.setValue('timeframe', value)}
                      defaultValue={walkForwardForm.watch('timeframe')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1M">1 Minute</SelectItem>
                        <SelectItem value="5M">5 Minutes</SelectItem>
                        <SelectItem value="15M">15 Minutes</SelectItem>
                        <SelectItem value="1H">1 Hour</SelectItem>
                        <SelectItem value="4H">4 Hours</SelectItem>
                        <SelectItem value="1D">1 Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="train_bars">Train Bars</Label>
                    <Input
                      id="train_bars"
                      type="number"
                      {...walkForwardForm.register('train_bars')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="test_bars">Test Bars</Label>
                    <Input
                      id="test_bars"
                      type="number"
                      {...walkForwardForm.register('test_bars')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="num_folds">Number of Folds</Label>
                    <Input
                      id="num_folds"
                      type="number"
                      {...walkForwardForm.register('num_folds')}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={walkForwardMutation.isPending}
                >
                  {walkForwardMutation.isPending ? 'Running Validation...' : 'Run Walk-Forward'}
                </Button>
              </form>

              {walkForwardMutation.isError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-800">
                    Error: {walkForwardMutation.error instanceof Error ? walkForwardMutation.error.message : 'Validation failed'}
                  </p>
                </div>
              )}

              {walkForwardResult && (
                <div className="mt-6 space-y-4">
                  <h3 className="text-lg font-semibold">Walk-Forward Results</h3>

                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">OOS Sharpe (Stitched)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">
                          {walkForwardResult.oos_sharpe_stitched.toFixed(2)}
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Fold Consistency</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">
                          {(walkForwardResult.fold_consistency * 100).toFixed(1)}%
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Avg Win Rate</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">
                          {(walkForwardResult.avg_win_rate * 100).toFixed(1)}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Overall Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total OOS P&L:</span>
                        <span className={`font-semibold ${walkForwardResult.total_oos_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {walkForwardResult.total_oos_pnl.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Trades:</span>
                        <span className="font-semibold">{walkForwardResult.total_oos_trades}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Max Drawdown:</span>
                        <span className="font-semibold text-red-600">
                          {(walkForwardResult.max_drawdown * 100).toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mean Fold Sharpe:</span>
                        <span className="font-semibold">
                          {walkForwardResult.fold_sharpe_mean.toFixed(2)} ± {walkForwardResult.fold_sharpe_std.toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Fold-by-Fold Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {walkForwardResult.folds.map((fold) => (
                          <div
                            key={fold.fold_id}
                            className="p-3 border rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-semibold">Fold {fold.fold_id}</span>
                              <span className={`font-bold ${fold.oos_sharpe >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                Sharpe: {fold.oos_sharpe.toFixed(2)}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-sm text-muted-foreground">
                              <div>
                                <div>P&L:</div>
                                <div className={fold.oos_pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {fold.oos_pnl.toFixed(2)}
                                </div>
                              </div>
                              <div>
                                <div>Win Rate:</div>
                                <div>{(fold.win_rate * 100).toFixed(1)}%</div>
                              </div>
                              <div>
                                <div>Trades:</div>
                                <div>{fold.total_trades}</div>
                              </div>
                              <div>
                                <div>Max DD:</div>
                                <div className="text-red-600">{(fold.max_drawdown * 100).toFixed(1)}%</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
